import React, { useEffect, useMemo, useRef, useState } from "react";
import { Line } from "react-chartjs-2";
import { formatLocalAxisTime, formatLocalDateTime } from "../../../utils/dateTime";
import { chartTimePoint, liveTimeExtent, liveZoomSource, minMaxSample, planLiveAxis, rememberLiveEdge } from "../../../utils/liveHistory";
import { gapSegment } from "../../../utils/statusGaps";

const COMBINED_CHART_ID = "combined";
const EMPTY_ROWS = [];
const contrastColor = (theme) => (theme === "dark" ? "#F8FAFC" : "#0F172A");
const defaultGridHex = (theme) => (theme === "dark" ? "#94A3B8" : "#64748B");
const defaultGridOpacity = (theme) => (theme === "dark" ? 0.4 : 0.55);
const hexToRgba = (hex, opacity) => {
    const raw = String(hex || "").replace("#", "");
    const value = Number.parseInt(raw, 16);
    if (!Number.isFinite(value) || raw.length !== 6) return `rgba(100, 116, 139, ${opacity})`;
    return `rgba(${(value >> 16) & 255}, ${(value >> 8) & 255}, ${value & 255}, ${opacity})`;
};

const useNearViewport = (rootMargin = "600px") => {
    const elementRef = useRef(null);
    const [nearViewport, setNearViewport] = useState(true);
    useEffect(() => {
        const element = elementRef.current;
        if (!element || typeof IntersectionObserver === "undefined") return undefined;
        const observer = new IntersectionObserver(
            ([entry]) => setNearViewport(Boolean(entry?.isIntersecting)),
            { root: null, rootMargin, threshold: 0 }
        );
        observer.observe(element);
        return () => observer.disconnect();
    }, [rootMargin]);
    return [elementRef, nearViewport];
};

const queuedUpdates = new WeakMap();
const updateChartOnce = (chart) => {
    if (!chart || queuedUpdates.has(chart)) return;
    const schedule = typeof requestAnimationFrame === "function"
        ? requestAnimationFrame
        : (callback) => setTimeout(callback, 0);
    const handle = schedule(() => {
        queuedUpdates.delete(chart);
        if (chart.ctx && chart.canvas) chart.update("none");
    });
    queuedUpdates.set(chart, handle);
};

const readScaleRange = (scale) => {
    if (!scale) {
        return null;
    }
    const min = Number.isFinite(scale.options?.min) ? scale.options.min : scale.min;
    const max = Number.isFinite(scale.options?.max) ? scale.options.max : scale.max;
    if (!Number.isFinite(min) || !Number.isFinite(max)) {
        return null;
    }
    return { min, max };
};

const applyChartXRange = (chart, min, max, { releaseZoom = false } = {}) => {
    const xScale = chart?.options?.scales?.x;
    if (!xScale || !Number.isFinite(min) || !Number.isFinite(max) || !(max > min)) {
        return false;
    }
    chart.stop?.();
    xScale.min = min;
    xScale.max = max;
    updateChartOnce(chart);
    if (releaseZoom && typeof chart.resetZoom === "function") {
        chart.resetZoom("none");
    }
    return true;
};

const applyLiveTimeViewport = (chart, rows, { followLatest, viewport, previousLatest }) => {
    const extent = liveTimeExtent(rows);
    const plan = planLiveAxis({ followLatest, viewport, previousLatest, extent });
    if (!plan || plan.mode === "none") {
        return null;
    }
    applyChartXRange(chart, plan.min, plan.max, { releaseZoom: plan.mode === "fit" });
    return { ...plan, latest: extent.latest };
};

const syncLiveChartViewport = (chart, rows, {
    followLatest,
    preserveViewport,
    viewportRef,
    liveEdgeRef,
    onResumeFollow,
}) => {
    const result = applyLiveTimeViewport(chart, rows, {
        followLatest,
        viewport: followLatest ? null : (preserveViewport ? viewportRef.current : null),
        previousLatest: liveEdgeRef.current,
    });
    if (!result) {
        return;
    }
    liveEdgeRef.current = result.latest;
    viewportRef.current = result.mode === "fit" ? null : { min: result.min, max: result.max };
    if (result.resumeFollow) {
        onResumeFollow?.();
    }
};

const timeScale = (chrome) => ({
    type: "time",
    bounds: "data",
    time: {
        tooltipFormat: "yyyy-MM-dd HH:mm:ss.SSS",
        displayFormats: {
            millisecond: "HH:mm:ss.SSS",
            second: "HH:mm:ss",
            minute: "HH:mm",
            hour: "HH:mm",
            day: "dd MMM",
        },
    },
    title: { display: true, text: "Time", color: chrome.label },
    ticks: {
        color: chrome.tick,
        maxRotation: 0,
        autoSkip: true,
        maxTicksLimit: 8,
        callback(value) {
            return formatLocalAxisTime(value);
        },
    },
    grid: { color: chrome.grid, lineWidth: 1 },
    border: { color: chrome.frame },
});

const axisChrome = (theme, appearance) => {
    const gridHex = appearance.gridColor || defaultGridHex(theme);
    const gridOpacity = appearance.gridOpacity == null ? defaultGridOpacity(theme) : appearance.gridOpacity;
    return {
        tick: theme === "dark" ? "#CBD5E1" : "#475569",
        label: theme === "dark" ? "#F1F5F9" : "#0F172A",
        grid: hexToRgba(gridHex, gridOpacity),
        frame: gridHex,
    };
};

const isSelectedPoint = (selection, seriesId, index) => (
    Boolean(selection) && selection.seriesId === seriesId && selection.index === index
);

const pointVisuals = (color, seriesId, appearance, theme, selectionRef) => ({
    borderColor: color,
    backgroundColor: color,
    borderWidth: appearance.lineWidth,
    tension: 0,
    fill: false,
    spanGaps: false,
    borderCapStyle: "round",
    borderJoinStyle: "round",
    pointBackgroundColor: color,
    pointHoverBackgroundColor: color,
    pointHoverBorderColor: contrastColor(theme),
    pointHoverBorderWidth: 2,
    pointHitRadius: 12,
    pointBorderColor(context) {
        return isSelectedPoint(selectionRef.current, seriesId, context.dataIndex)
            ? contrastColor(theme)
            : color;
    },
    pointBorderWidth(context) {
        return isSelectedPoint(selectionRef.current, seriesId, context.dataIndex) ? 2.5 : 0;
    },
    pointRadius(context) {
        if (isSelectedPoint(selectionRef.current, seriesId, context.dataIndex)) {
            return Math.max(Number(appearance.pointSize) + 4, 7);
        }
        const denseSeries = (context.dataset?.data?.length || 0) > 1500;
        return appearance.showPoints && !denseSeries ? appearance.pointSize : 0;
    },
    pointHoverRadius(context) {
        if (isSelectedPoint(selectionRef.current, seriesId, context.dataIndex)) {
            return Math.max(Number(appearance.pointSize) + 4, 7);
        }
        return Math.max(appearance.showPoints ? appearance.pointSize : 4, 4);
    },
});

const zoomPluginOptions = (theme, onChange, minRange) => ({
    pan: {
        enabled: true,
        mode: "x",
        modifierKey: "shift",
        threshold: 8,
        onPanComplete: ({ chart: pannedChart }) => onChange(pannedChart, "user"),
    },
    limits: {
        x: { min: "original", max: "original", minRange },
    },
    zoom: {
        mode: "x",
        wheel: {
            enabled: true,
            speed: 0.08,
        },
        pinch: {
            enabled: true,
        },
        drag: {
            enabled: true,
            threshold: 8,
            drawTime: "afterDatasetsDraw",
            backgroundColor: theme === "dark" ? "rgba(56, 189, 248, 0.18)" : "rgba(37, 99, 235, 0.16)",
            borderColor: theme === "dark" ? "#38BDF8" : "#2563EB",
            borderWidth: 1,
        },
        onZoom: ({ chart: zoomedChart, trigger }) => onChange(
            zoomedChart,
            trigger && trigger !== "api" ? "user" : "api"
        ),
        onZoomComplete: ({ chart: zoomedChart }) => onChange(zoomedChart, "api"),
    },
});

const revealChartPoint = (chart, index) => {
    const scale = chart.scales?.x;
    if (!scale || typeof chart.zoomScale !== "function") {
        return;
    }
    if (index >= scale.min && index <= scale.max) {
        return;
    }
    const bounds = chart.getInitialScaleBounds?.().x || {};
    const originalMin = Number.isFinite(bounds.min) ? bounds.min : 0;
    const originalMax = Number.isFinite(bounds.max) ? bounds.max : Math.max(0, (chart.data.labels?.length || 1) - 1);
    const span = Math.max(scale.max - scale.min, 8);
    let min = index - span / 2;
    let max = index + span / 2;
    if (min < originalMin) {
        max += originalMin - min;
        min = originalMin;
    }
    if (max > originalMax) {
        min -= max - originalMax;
        max = originalMax;
    }
    chart.zoomScale("x", {
        min: Math.max(originalMin, min),
        max: Math.min(originalMax, max),
    }, "none");
};

const revealTimePoint = (chart, xValue) => {
    const scale = chart.scales?.x;
    if (!scale || !Number.isFinite(xValue) || typeof chart.zoomScale !== "function") {
        return;
    }
    if (xValue >= scale.min && xValue <= scale.max) {
        return;
    }
    const bounds = chart.getInitialScaleBounds?.().x || {};
    const originalMin = Number.isFinite(bounds.min) ? bounds.min : scale.min;
    const originalMax = Number.isFinite(bounds.max) ? bounds.max : scale.max;
    const span = Math.max(scale.max - scale.min, 60 * 1000);
    let min = xValue - span / 2;
    let max = xValue + span / 2;
    if (min < originalMin) {
        max += originalMin - min;
        min = originalMin;
    }
    if (max > originalMax) {
        min -= max - originalMax;
        max = originalMax;
    }
    chart.zoomScale("x", {
        min: Math.max(originalMin, min),
        max: Math.min(originalMax, max),
    }, "none");
};

const TagTrend = ({
    chart,
    color,
    label,
    theme,
    appearance,
    selectedIndex,
    zoomed,
    axisMode,
    preserveViewport,
    followLatest,
    followEpoch,
    onSelect,
    onZoomChange,
    onManualZoom,
    onResumeFollow,
    onResetZoom,
    cursorActive,
    onClearCursor,
}) => {
    const chartRef = useRef(null);
    const [containerRef, nearViewport] = useNearViewport("600px");
    const selectionRef = useRef(null);
    const viewportRef = useRef(null);
    const liveEdgeRef = useRef(null);
    const rows = Array.isArray(chart.tableData) ? chart.tableData : EMPTY_ROWS;
    const selectedRecordId = selectedIndex == null ? null : rows[selectedIndex]?.id ?? null;
    const sampledRows = useMemo(
        () => minMaxSample(rows, 1000, selectedRecordId),
        [rows, selectedRecordId]
    );
    const [renderRows, setRenderRows] = useState(sampledRows);
    const [renderSourceRows, setRenderSourceRows] = useState(rows);
    const [renderStatusEvents, setRenderStatusEvents] = useState(chart.statusEvents);
    useEffect(() => {
        if (nearViewport) {
            setRenderRows(sampledRows);
            setRenderSourceRows(rows);
            setRenderStatusEvents(chart.statusEvents);
        }
    }, [chart.statusEvents, nearViewport, rows, sampledRows]);
    const displayIndex = selectedRecordId == null
        ? null
        : renderRows.findIndex((row) => row?.id === selectedRecordId);
    selectionRef.current = displayIndex == null || displayIndex < 0
        ? null
        : { seriesId: chart.id, index: displayIndex };
    const hidden = chart.visible === false;
    const useTimeAxis = axisMode === "time";
    const hasChartData = Boolean(chart.chartData);
    const baseChartData = useTimeAxis ? null : chart.chartData;

    const data = useMemo(() => {
        if (!hasChartData) {
            return null;
        }
        const visuals = {
            ...pointVisuals(color, chart.id, appearance, theme, selectionRef),
            seriesId: chart.id,
            label: label || chart.tagName || `Tag ${chart.tagId}`,
            hidden,
        };
        if (useTimeAxis) {
            return {
                datasets: [
                    {
                        ...visuals,
                        parsing: false,
                        segment: gapSegment(renderStatusEvents),
                        spanGaps: false,
                        data: renderRows.map((row) => chartTimePoint(row) || { x: null, y: null }),
                    },
                ],
            };
        }
        return {
            ...baseChartData,
            datasets: baseChartData.datasets.map((dataset) => ({
                ...dataset,
                ...visuals,
            })),
        };
    }, [appearance, baseChartData, chart.id, chart.tagId, chart.tagName, color, hasChartData, hidden, label, renderRows, renderStatusEvents, theme, useTimeAxis]);

    const options = useMemo(() => {
        const chrome = axisChrome(theme, appearance);
        const tagName = label || chart.tagName || `Tag ${chart.tagId}`;
        const reportZoom = (instance, source) => {
            const range = readScaleRange(instance?.scales?.x);
            const extent = liveTimeExtent(renderRows);
            if (range) {
                viewportRef.current = range;
            }
            if (source === "user") {
                liveEdgeRef.current = rememberLiveEdge(liveEdgeRef.current, range?.max, extent);
            }
            onZoomChange(
                chart.id,
                instance,
                liveZoomSource(source, range?.min, range?.max, extent)
            );
        };

        return {
            responsive: true,
            maintainAspectRatio: false,
            animation: false,
            normalized: true,
            interaction: {
                mode: "nearest",
                intersect: true,
                axis: "xy",
            },
            onClick: (_event, elements) => {
                const hit = elements?.[0];
                if (!hit) {
                    return;
                }
                const recordId = renderRows[hit.index]?.id;
                const sourceIndex = renderSourceRows.findIndex((row) => row?.id === recordId);
                if (sourceIndex >= 0) onSelect(chart.id, sourceIndex);
            },
            plugins: {
                decimation: {
                    enabled: false,
                },
                legend: {
                    labels: {
                        color: chrome.label,
                        usePointStyle: true,
                        font: {
                            family: '"Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
                        },
                    },
                },
                tooltip: {
                    callbacks: {
                        title(items) {
                            const index = items[0]?.dataIndex;
                            const row = renderRows[index];
                            return row ? formatLocalDateTime(row.timestamp) : "";
                        },
                        label(item) {
                            const row = renderRows[item.dataIndex];
                            const value = row ? row.value : item.parsed.y;
                            return `${tagName}: ${value}`;
                        },
                    },
                },
                zoom: zoomPluginOptions(theme, reportZoom, useTimeAxis ? 1 : 2),
            },
            scales: {
                x: useTimeAxis ? timeScale(chrome) : {
                    title: { display: true, text: "Time", color: chrome.label },
                    ticks: { color: chrome.tick, maxRotation: 0, autoSkip: true, maxTicksLimit: 8 },
                    grid: { color: chrome.grid, lineWidth: 1 },
                    border: { color: chrome.frame },
                },
                y: {
                    title: { display: true, text: "Value", color: chrome.label },
                    ticks: { color: chrome.tick },
                    grid: { color: chrome.grid, lineWidth: 1 },
                    border: { color: chrome.frame },
                },
            },
        };
    }, [appearance, chart.id, chart.tagId, chart.tagName, label, onSelect, onZoomChange, renderRows, renderSourceRows, theme, useTimeAxis]);

    useEffect(() => {
        viewportRef.current = null;
        const instance = chartRef.current;
        if (instance && typeof instance.resetZoom === "function") {
            instance.resetZoom("none");
        }
    }, [useTimeAxis]);

    useEffect(() => {
        const instance = chartRef.current;
        if (!instance || !nearViewport) {
            return;
        }
        if (useTimeAxis && (followLatest || preserveViewport)) {
            syncLiveChartViewport(instance, rows, {
                followLatest,
                preserveViewport,
                viewportRef,
                liveEdgeRef,
                onResumeFollow,
            });
            return;
        }
        if (typeof instance.resetZoom !== "function") {
            return;
        }
        viewportRef.current = null;
        liveEdgeRef.current = null;
        instance.resetZoom("none");
    }, [chart.chartData, followLatest, nearViewport, onResumeFollow, preserveViewport, rows, useTimeAxis]);

    useEffect(() => {
        if (!followLatest) {
            return;
        }
        const instance = chartRef.current;
        if (!instance || !nearViewport) {
            return;
        }
        if (useTimeAxis) {
            syncLiveChartViewport(instance, rows, {
                followLatest: true,
                preserveViewport: false,
                viewportRef,
                liveEdgeRef,
                onResumeFollow,
            });
            return;
        }
        viewportRef.current = null;
        liveEdgeRef.current = null;
        if (typeof instance.resetZoom === "function") {
            instance.resetZoom("none");
        }
    }, [followEpoch, followLatest, nearViewport, onResumeFollow, rows, useTimeAxis]);

    useEffect(() => {
        const instance = chartRef.current;
        if (!instance || !nearViewport) {
            return;
        }
        if (selectedIndex == null) {
            if (!instance.$selectionGuide) return;
            instance.$selectionGuide = null;
            instance.setActiveElements?.([]);
            updateChartOnce(instance);
            return;
        }
        if (displayIndex == null || displayIndex < 0) return;
        if (useTimeAxis) {
            const row = rows[selectedIndex];
            revealTimePoint(instance, chartTimePoint(row)?.x ?? NaN);
        } else {
            revealChartPoint(instance, selectedIndex);
        }
        instance.$selectionGuide = {
            index: displayIndex,
            datasetIndex: 0,
            color,
            contrast: contrastColor(theme),
        };
        instance.setActiveElements?.([{ datasetIndex: 0, index: displayIndex }]);
        const point = instance.getDatasetMeta(0)?.data?.[displayIndex];
        if (point && instance.tooltip) {
            instance.tooltip.setActiveElements(
                [{ datasetIndex: 0, index: displayIndex }],
                { x: point.x, y: point.y }
            );
        }
        updateChartOnce(instance);
        onZoomChange(chart.id, instance, "api");
    }, [chart.id, color, displayIndex, nearViewport, onZoomChange, rows, selectedIndex, theme, useTimeAxis]);

    if (!data) {
        return null;
    }

    const selectedRow = selectedIndex == null ? null : rows[selectedIndex] || null;

    return (
        <article ref={containerRef} className="graph-page__card graph-page__chart-card">
            <div className="graph-page__card-head graph-page__card-head--split">
                <div>
                    <h2>{label || chart.tagName || `Tag ${chart.tagId}`}</h2>
                    <p>{rows.length} rows</p>
                </div>
                <span className="graph-page__swatch" style={{ background: color }} aria-hidden="true" />
            </div>
            <div className="graph-page__zoom">
                <button type="button" className="graph-page__button" onClick={() => { onManualZoom?.(); chartRef.current?.zoom?.({ x: 1.2, y: 1 }); }}>
                    Zoom In
                </button>
                <button type="button" className="graph-page__button" onClick={() => { onManualZoom?.(); chartRef.current?.zoom?.({ x: 0.8, y: 1 }); }}>
                    Zoom Out
                </button>
                <button type="button" className="graph-page__button" onClick={() => onResetZoom?.(chartRef.current)} disabled={!zoomed}>
                    Reset Zoom
                </button>
                <button type="button" className="graph-page__button" onClick={onClearCursor} disabled={!cursorActive}>
                    Clear Cursor
                </button>
                {zoomed && <span className="graph-page__zoom-state">Zoomed range</span>}
            </div>
            <p className="graph-page__hint">Wheel zooms the time axis. Drag a range to zoom. Shift-drag pans across the loaded samples.</p>
            {selectedRow && (
                <p className="graph-page__selection" role="status">
                    Selected {label || chart.tagName || `Tag ${chart.tagId}`} · {formatLocalDateTime(selectedRow.timestamp)} · {selectedRow.value}
                </p>
            )}
            {hidden && <p className="graph-page__hint">This series is hidden.</p>}
            <div className="graph-page__plot">
                <Line ref={chartRef} datasetIdKey="seriesId" data={data} options={options} />
            </div>
        </article>
    );
};

const CombinedTrend = ({
    series,
    theme,
    appearance,
    selection,
    zoomed,
    preserveViewport,
    followLatest,
    followEpoch,
    onSelect,
    onZoomChange,
    onToggleVisible,
    onManualZoom,
    onResumeFollow,
    onResetZoom,
    cursorActive,
    onClearCursor,
}) => {
    const chartRef = useRef(null);
    const selectionRef = useRef(selection);
    const viewportRef = useRef(null);
    const liveEdgeRef = useRef(null);
    const dataIdentity = useRef({ next: 1, map: new WeakMap() });
    selectionRef.current = selection;
    const seriesKey = series.map((item) => {
        const chartData = item.chart.chartData;
        if (!chartData) {
            return `${item.chart.id}:0`;
        }
        let identity = dataIdentity.current.map.get(chartData);
        if (!identity) {
            identity = dataIdentity.current.next;
            dataIdentity.current.next += 1;
            dataIdentity.current.map.set(chartData, identity);
        }
        return `${item.chart.id}:${identity}`;
    }).join("|");

    const displaySeries = useMemo(() => series.map((item) => {
        const rows = Array.isArray(item.chart.tableData) ? item.chart.tableData : EMPTY_ROWS;
        const selectedId = selection?.seriesId === item.chart.id
            ? rows[selection.index]?.id ?? null
            : null;
        return { ...item, displayRows: minMaxSample(rows, 1000, selectedId) };
    }), [selection, series]);

    const data = useMemo(() => ({
        datasets: displaySeries.map((item) => {
            return {
                ...pointVisuals(item.color, item.chart.id, appearance, theme, selectionRef),
                seriesId: item.chart.id,
                label: item.label,
                hidden: item.chart.visible === false,
                yAxisID: item.chart.yAxis === "y1" ? "y1" : "y",
                parsing: false,
                segment: gapSegment(item.chart.statusEvents),
                spanGaps: false,
                data: item.displayRows.map((row) => chartTimePoint(row) || { x: null, y: null }),
            };
        }),
    }), [appearance, displaySeries, theme]);

    const options = useMemo(() => {
        const chrome = axisChrome(theme, appearance);
        const showRight = series.some((item) => item.chart.visible !== false && item.chart.yAxis === "y1");
        const showLeft = series.some((item) => item.chart.visible !== false && item.chart.yAxis !== "y1");
        const reportZoom = (instance, source) => {
            const range = readScaleRange(instance?.scales?.x);
            const rows = series.flatMap((item) => (Array.isArray(item.chart.tableData) ? item.chart.tableData : []));
            const extent = liveTimeExtent(rows);
            if (range) {
                viewportRef.current = range;
            }
            if (source === "user") {
                liveEdgeRef.current = rememberLiveEdge(liveEdgeRef.current, range?.max, extent);
            }
            onZoomChange(
                COMBINED_CHART_ID,
                instance,
                liveZoomSource(source, range?.min, range?.max, extent)
            );
        };

        return {
            responsive: true,
            maintainAspectRatio: false,
            animation: false,
            normalized: true,
            interaction: {
                mode: "nearest",
                intersect: true,
                axis: "xy",
            },
            onClick: (_event, elements, clickedChart) => {
                const hit = elements?.[0];
                const dataset = hit ? clickedChart.data.datasets[hit.datasetIndex] : null;
                if (!hit || dataset?.seriesId == null) {
                    return;
                }
                const source = displaySeries[hit.datasetIndex];
                const recordId = source?.displayRows?.[hit.index]?.id;
                const sourceIndex = source?.chart.tableData?.findIndex((row) => row?.id === recordId) ?? -1;
                if (sourceIndex >= 0) onSelect(dataset.seriesId, sourceIndex);
            },
            plugins: {
                decimation: {
                    enabled: false,
                },
                legend: {
                    labels: {
                        color: chrome.label,
                        usePointStyle: true,
                        font: {
                            family: '"Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
                        },
                    },
                    onClick(_event, legendItem) {
                        const dataset = series[legendItem.datasetIndex];
                        if (dataset) {
                            onToggleVisible(dataset.chart.id);
                        }
                    },
                },
                tooltip: {
                    callbacks: {
                        title(items) {
                            const item = items[0];
                            const source = item ? displaySeries[item.datasetIndex] : null;
                            const row = source?.displayRows?.[item.dataIndex];
                            return row ? formatLocalDateTime(row.timestamp) : "";
                        },
                        label(item) {
                            const source = displaySeries[item.datasetIndex];
                            const row = source?.displayRows?.[item.dataIndex];
                            const name = source?.label || "Tag";
                            const value = row ? row.value : item.parsed.y;
                            return `${name}: ${value}`;
                        },
                    },
                },
                zoom: zoomPluginOptions(theme, reportZoom, 1),
            },
            scales: {
                x: timeScale(chrome),
                y: {
                    type: "linear",
                    position: "left",
                    display: showLeft || !showRight,
                    title: { display: true, text: showRight ? "Left value" : "Value", color: chrome.label },
                    ticks: { color: chrome.tick },
                    grid: { color: chrome.grid, lineWidth: 1, drawOnChartArea: showLeft || !showRight },
                    border: { color: chrome.frame },
                },
                y1: {
                    type: "linear",
                    position: "right",
                    display: showRight,
                    title: { display: true, text: "Right value", color: chrome.label },
                    ticks: { color: chrome.tick },
                    grid: { color: chrome.grid, lineWidth: 1, drawOnChartArea: !showLeft && showRight },
                    border: { color: chrome.frame },
                },
            },
        };
    }, [appearance, displaySeries, onSelect, onToggleVisible, onZoomChange, series, theme]);

    useEffect(() => {
        const instance = chartRef.current;
        if (!instance) {
            return;
        }
        const rows = series.flatMap((item) => (Array.isArray(item.chart.tableData) ? item.chart.tableData : []));
        if (followLatest || preserveViewport) {
            syncLiveChartViewport(instance, rows, {
                followLatest,
                preserveViewport,
                viewportRef,
                liveEdgeRef,
                onResumeFollow,
            });
            return;
        }
        if (typeof instance.resetZoom !== "function") {
            return;
        }
        viewportRef.current = null;
        liveEdgeRef.current = null;
        instance.resetZoom("none");
        onZoomChange(COMBINED_CHART_ID, instance, "api");
    }, [followLatest, onResumeFollow, onZoomChange, preserveViewport, series, seriesKey]);

    useEffect(() => {
        if (!followLatest) {
            return;
        }
        const instance = chartRef.current;
        if (!instance) {
            return;
        }
        const rows = series.flatMap((item) => (Array.isArray(item.chart.tableData) ? item.chart.tableData : []));
        syncLiveChartViewport(instance, rows, {
            followLatest: true,
            preserveViewport: false,
            viewportRef,
            liveEdgeRef,
            onResumeFollow,
        });
    }, [followEpoch, followLatest, onResumeFollow, series]);

    useEffect(() => {
        const instance = chartRef.current;
        if (!instance) {
            return;
        }
        const datasetIndex = selection
            ? instance.data.datasets.findIndex((dataset) => dataset.seriesId === selection.seriesId)
            : -1;
        const meta = datasetIndex >= 0 ? instance.getDatasetMeta(datasetIndex) : null;
        if (!selection || datasetIndex < 0 || !meta || meta.hidden) {
            instance.$selectionGuide = null;
            instance.setActiveElements?.([]);
            updateChartOnce(instance);
            return;
        }
        const source = series.find((item) => item.chart.id === selection.seriesId);
        const row = source?.chart.tableData?.[selection.index];
        const displaySource = displaySeries.find((item) => item.chart.id === selection.seriesId);
        const displayIndex = displaySource?.displayRows?.findIndex((item) => item?.id === row?.id) ?? -1;
        if (displayIndex < 0) return;
        revealTimePoint(instance, chartTimePoint(row)?.x ?? NaN);
        instance.$selectionGuide = {
            index: displayIndex,
            datasetIndex,
            color: source?.color || contrastColor(theme),
            contrast: contrastColor(theme),
        };
        instance.setActiveElements?.([{ datasetIndex, index: displayIndex }]);
        const point = instance.getDatasetMeta(datasetIndex)?.data?.[displayIndex];
        if (point && !point.skip && instance.tooltip) {
            instance.tooltip.setActiveElements(
                [{ datasetIndex, index: displayIndex }],
                { x: point.x, y: point.y }
            );
        }
        updateChartOnce(instance);
        onZoomChange(COMBINED_CHART_ID, instance, "api");
    }, [displaySeries, onZoomChange, selection, series, theme]);

    const selectedSource = selection
        ? series.find((item) => item.chart.id === selection.seriesId)
        : null;
    const selectedRow = selectedSource?.chart.tableData?.[selection.index] || null;

    return (
        <article className="graph-page__card graph-page__chart-card">
            <div className="graph-page__card-head graph-page__card-head--split">
                <div>
                    <h2>Combined chart</h2>
                    <p>{series.length} series on one time axis</p>
                </div>
            </div>
            <div className="graph-page__zoom">
                <button type="button" className="graph-page__button" onClick={() => { onManualZoom?.(); chartRef.current?.zoom?.({ x: 1.2, y: 1 }); }}>
                    Zoom In
                </button>
                <button type="button" className="graph-page__button" onClick={() => { onManualZoom?.(); chartRef.current?.zoom?.({ x: 0.8, y: 1 }); }}>
                    Zoom Out
                </button>
                <button type="button" className="graph-page__button" onClick={() => onResetZoom?.(chartRef.current)} disabled={!zoomed}>
                    Reset Zoom
                </button>
                <button type="button" className="graph-page__button" onClick={onClearCursor} disabled={!cursorActive}>
                    Clear Cursor
                </button>
                {zoomed && <span className="graph-page__zoom-state">Zoomed range</span>}
            </div>
            <p className="graph-page__hint">
                Each series keeps its own timestamps. Wheel zooms the time axis. Drag a range to zoom. Shift-drag pans. Click the legend to hide a series.
            </p>
            {selectedRow && (
                <p className="graph-page__selection" role="status">
                    Selected {selectedSource.label} · {formatLocalDateTime(selectedRow.timestamp)} · {selectedRow.value}
                    {selectedSource.chart.visible === false ? " · series hidden" : ""}
                </p>
            )}
            <div className="graph-page__plot">
                <Line ref={chartRef} datasetIdKey="seriesId" data={data} options={options} />
            </div>
        </article>
    );
};

export { TagTrend, CombinedTrend };
